import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Bell, Save, Send, ExternalLink, Info, ShieldCheck } from "lucide-react";

/**
 * User configures their own Telegram bot + chat_id here.
 * Each user has their own bot so ANATRADEX never holds a shared secret.
 */
export default function AlertsSettings() {
  const [form, setForm] = useState({
    telegram_bot_token: "",
    telegram_chat_id: "",
    alerts_daily_loss_pct: 80,
    alerts_total_loss_pct: 80,
    alerts_target_reached: true,
    alerts_bridge_offline_min: 10,
    alerts_killzone: false,
    killzone_symbols: "EURUSD",
    enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem("auth_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/notifications/settings`, { headers: authHeader() });
        setForm(f => ({ ...f, ...r.data }));
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    try {
      await axios.put(`${API}/notifications/settings`, form, { headers: authHeader() });
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const testSend = async () => {
    setTesting(true);
    try {
      await save();
      await axios.post(`${API}/notifications/test`, { message: "🚀 ANATRADEX — Test réussi ! Vos alertes sont opérationnelles." },
        { headers: authHeader() });
      toast.success("Message de test envoyé — vérifiez Telegram !");
    } catch (e) {
      toast.error("Test échoué : " + (e.response?.data?.detail || e.message));
    } finally { setTesting(false); }
  };

  if (loading) return <div className="p-6 text-white/50">Chargement…</div>;

  return (
    <div className="p-6 max-w-3xl" data-testid="alerts-settings-page">
      <div className="flex items-center gap-3 mb-2">
        <Bell size={22} className="text-accent-blue" />
        <h1 className="font-heading font-bold text-2xl">Alertes Telegram</h1>
        {form.enabled && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-accent-green/15 text-accent-green border border-accent-green/30">
            Activées
          </span>
        )}
      </div>
      <p className="text-white/60 text-sm mb-6 max-w-2xl">
        Recevez des notifications Telegram dès qu'une règle prop firm est proche du seuil critique.
        Configurez votre propre bot (60 secondes) — ANATRADEX ne conserve aucun secret partagé.
      </p>

      {/* Setup guide */}
      <div className="panel p-5 mb-4 border-l-2 border-accent-blue" data-testid="alerts-setup-guide">
        <div className="font-heading font-semibold mb-3 flex items-center gap-2">
          <Info size={16} className="text-accent-blue" /> Comment créer votre bot Telegram (60 sec)
        </div>
        <ol className="space-y-2 text-sm text-white/70 list-decimal list-inside">
          <li>
            Ouvrez Telegram → cherchez{" "}
            <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer"
               className="text-accent-blue hover:underline inline-flex items-center gap-1">
              @BotFather <ExternalLink size={11} />
            </a>{" "}
            → tapez <code className="font-mono text-xs bg-white/5 px-1 rounded">/newbot</code>
          </li>
          <li>Choisissez un nom (ex : <em>Mes Alertes Trading</em>) et un username finissant par <code className="font-mono text-xs bg-white/5 px-1 rounded">_bot</code></li>
          <li>Copiez le <strong className="text-white">token</strong> qui ressemble à <code className="font-mono text-xs bg-white/5 px-1 rounded">1234567890:AAH…</code> — collez-le ci-dessous</li>
          <li>Envoyez un message quelconque à votre nouveau bot (dites-lui « Salut ! »)</li>
          <li>
            Ouvrez <a href="https://api.telegram.org/bot" target="_blank" rel="noopener noreferrer"
                     className="text-accent-blue hover:underline">l'URL des updates</a> :{" "}
            <code className="font-mono text-xs bg-white/5 px-1 rounded">
              https://api.telegram.org/bot&lt;VOTRE_TOKEN&gt;/getUpdates
            </code>
          </li>
          <li>Cherchez <code className="font-mono text-xs bg-white/5 px-1 rounded">"chat":{"{"}"id":123456789{"}"}</code> — c'est votre <strong className="text-white">chat_id</strong>, collez-le ci-dessous</li>
        </ol>
      </div>

      <div className="panel p-5 mb-4">
        <FormRow label="Bot token">
          <input type="password" className="input w-full font-mono text-sm" placeholder="1234567890:AAHxxx…"
                 value={form.telegram_bot_token || ""}
                 onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })}
                 data-testid="alerts-bot-token" />
        </FormRow>
        <FormRow label="Chat ID">
          <input type="text" className="input w-full font-mono text-sm" placeholder="123456789"
                 value={form.telegram_chat_id || ""}
                 onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
                 data-testid="alerts-chat-id" />
        </FormRow>

        <hr className="border-white/10 my-4" />

        <div className="text-xs uppercase tracking-wider text-white/50 mb-3">Seuils d'alerte</div>
        <FormRow label="Max Daily Loss ≥">
          <div className="flex items-center gap-2">
            <input type="number" min={50} max={100} className="input w-24" value={form.alerts_daily_loss_pct}
                   onChange={(e) => setForm({ ...form, alerts_daily_loss_pct: parseFloat(e.target.value) })}
                   data-testid="alerts-daily-threshold" />
            <span className="text-white/50 text-xs">%</span>
          </div>
        </FormRow>
        <FormRow label="Max Total Loss ≥">
          <div className="flex items-center gap-2">
            <input type="number" min={50} max={100} className="input w-24" value={form.alerts_total_loss_pct}
                   onChange={(e) => setForm({ ...form, alerts_total_loss_pct: parseFloat(e.target.value) })} />
            <span className="text-white/50 text-xs">%</span>
          </div>
        </FormRow>
        <FormRow label="Alerte objectif atteint">
          <input type="checkbox" checked={form.alerts_target_reached}
                 onChange={(e) => setForm({ ...form, alerts_target_reached: e.target.checked })} />
        </FormRow>

        <hr className="border-white/10 my-4" />

        <div className="text-xs uppercase tracking-wider text-white/50 mb-1">Alertes Killzone (SMC)</div>
        <p className="text-[11px] text-white/45 mb-3 leading-relaxed">
          Pendant les killzones <b className="text-white/70">London (07h-10h UTC)</b> et{" "}
          <b className="text-white/70">New York (12h-15h &amp; 17h30-20h UTC)</b>, recevez une alerte quand
          le prix entre dans une zone <b className="text-white/70">non mitigée</b> (Order Block ou FVG, M15).
          Max 1 alerte par zone / 6h.
        </p>
        <FormRow label="Alertes killzone">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={!!form.alerts_killzone}
                   onChange={(e) => setForm({ ...form, alerts_killzone: e.target.checked })}
                   data-testid="alerts-killzone-toggle" />
            <span className="text-sm text-white/70">
              {form.alerts_killzone ? "Actif pendant les sessions London / NY" : "Désactivé"}
            </span>
          </label>
        </FormRow>
        <FormRow label="Symboles surveillés">
          <div>
            <input type="text" className="input w-full font-mono text-sm" placeholder="EURUSD, GBPUSD, XAUUSD"
                   value={form.killzone_symbols || ""}
                   onChange={(e) => setForm({ ...form, killzone_symbols: e.target.value })}
                   data-testid="alerts-killzone-symbols" />
            <div className="text-[10px] text-white/40 mt-1">Séparés par des virgules — 5 maximum.</div>
          </div>
        </FormRow>

        <hr className="border-white/10 my-4" />

        <FormRow label="Alertes activées">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={form.enabled}
                   onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                   data-testid="alerts-toggle" />
            <span className="text-sm text-white/70">
              {form.enabled ? "Actif — vous recevrez les alertes" : "Désactivé"}
            </span>
          </label>
        </FormRow>
      </div>

      <div className="flex justify-end gap-2">
        <button className="btn btn-secondary" onClick={testSend} disabled={testing || !form.telegram_bot_token || !form.telegram_chat_id}
                data-testid="alerts-test-btn">
          <Send size={14} /> {testing ? "Envoi..." : "Envoyer un message test"}
        </button>
        <button className="btn btn-primary" onClick={save} data-testid="alerts-save">
          <Save size={14} /> Enregistrer
        </button>
      </div>

      <div className="mt-6 text-[11px] text-white/40 flex gap-2">
        <ShieldCheck size={12} className="shrink-0 mt-0.5" />
        Votre bot token est stocké chiffré et n'est jamais partagé. Vous seul recevez les alertes.
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="grid grid-cols-[220px_1fr] items-center gap-3 mb-3">
      <span className="text-sm text-white/70">{label}</span>
      <div>{children}</div>
    </div>
  );
}
